import { useState } from 'react';
import { ManageableGymCard, FavoriteGymCard } from './GymCard';
import { Building2, Zap } from 'lucide-react';

const MyGymsTab = ({
  userGyms,
  selectedGym,
  onSelectGym,
  onSetPrimary,
  onRemoveGym,
  searchQuery,
  favoriteGyms = []
}) => {
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);
  const [removingGymId, setRemovingGymId] = useState(null);

  // Filter gyms based on search
  const filteredGyms = searchQuery.trim()
    ? userGyms.filter(gym =>
        gym.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        gym.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        gym.state?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : userGyms;

  // Filter favorites based on search
  const filteredFavorites = searchQuery.trim()
    ? favoriteGyms.filter(gym =>
        gym.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        gym.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        gym.state?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : favoriteGyms;

  // Get remaining gyms (not in favorites)
  const favoriteGymIds = new Set(favoriteGyms.map(g => g.gymId));
  const remainingGyms = filteredGyms.filter(gym => !favoriteGymIds.has(gym.gymId));

  const handleSetPrimary = async (gym) => {
    setSettingPrimaryId(gym.gymId);
    await onSetPrimary(gym);
    setSettingPrimaryId(null);
  };

  const handleRemove = async (gym) => {
    if (removingGymId === gym.gymId) {
      // User clicked again, actually remove
      await onRemoveGym(gym);
      setRemovingGymId(null);
    } else {
      // First click, show confirmation
      setRemovingGymId(gym.gymId);
      // Auto-cancel after 3 seconds
      setTimeout(() => {
        setRemovingGymId(null);
      }, 3000);
    }
  };

  // Empty state when user has no gyms
  if (userGyms.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="mb-4">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto" />
        </div>
        <h3 className="text-lg font-bold text-slate-700 mb-2">
          No gyms yet
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          You haven't joined any gyms yet
        </p>
        <p className="text-sm text-slate-600">
          Head to the <span className="font-bold">Find Gyms</span> tab to add your first gym
        </p>
      </div>
    );
  }

  // No search results
  if (searchQuery && filteredGyms.length === 0) {
    return (
      <div className="text-center py-12 px-4 text-slate-500">
        <p className="mb-2">No gyms match "{searchQuery}"</p>
        <p className="text-sm">Try the <span className="font-bold">Find Gyms</span> tab to add more gyms</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Favorites Section - Only show when we have favorites and not searching or when search matches favorites */}
      {filteredFavorites.length > 0 && (
        <div className="mb-6">
          <div className="mb-4">
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2">
              <span className="w-1 h-4 bg-teal-500 rounded"></span>
              Quick Access
            </h3>
            <p className="text-xs text-slate-500 mt-1 ml-3">
              Your most recent gyms for quick selection
            </p>
          </div>

          <div className="space-y-3">
            {filteredFavorites.map((gym) => (
              <FavoriteGymCard
                key={gym.gymId}
                gym={gym}
                isSelected={selectedGym?.gymId === gym.gymId}
                onClick={() => onSelectGym(gym)}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Other Gyms Section - Only show if we have remaining gyms */}
      {remainingGyms.length > 0 && (
        <div>
          <div className="mb-4">
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2">
              <span className="w-1 h-4 bg-purple-500 rounded"></span>
              {filteredFavorites.length > 0 ? 'All Gyms' : 'Your Gyms'}
            </h3>
            <p className="text-xs text-slate-500 mt-1 ml-3">
              {remainingGyms.length} {remainingGyms.length === 1 ? 'gym' : 'gyms'}
              {filteredFavorites.length > 0 ? ' • Tap to manage' : ' in your list'}
            </p>
          </div>

          <div className="space-y-3">
            {remainingGyms.map((gym) => (
              <div key={gym.gymId} className="relative">
                <ManageableGymCard
                  gym={gym}
                  isSelected={selectedGym?.gymId === gym.gymId}
                  onSelect={() => onSelectGym(gym)}
                  onSetPrimary={() => handleSetPrimary(gym)}
                  onRemove={() => handleRemove(gym)}
                  isSettingPrimary={settingPrimaryId === gym.gymId}
                />
                {removingGymId === gym.gymId && (
                  <div className="absolute inset-0 bg-coral-100 bg-opacity-95 border-2 border-coral-500 rounded-xl flex items-center justify-center p-4">
                    <div className="text-center">
                      <p className="text-sm font-bold text-coral-800 mb-2">
                        Remove this gym?
                      </p>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleRemove(gym)}
                          className="px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all text-sm touch-manipulation"
                        >
                          Yes, Remove
                        </button>
                        <button
                          onClick={() => setRemovingGymId(null)}
                          className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all text-sm touch-manipulation"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when search returns no results */}
      {searchQuery && filteredFavorites.length === 0 && remainingGyms.length === 0 && (
        <div className="text-center py-12 px-4 text-slate-500">
          <p className="mb-2">No gyms match "{searchQuery}"</p>
          <p className="text-sm">Try the <span className="font-bold">Find Gyms</span> tab to add more gyms</p>
        </div>
      )}
    </div>
  );
};

export default MyGymsTab;
